/**
 * FeatureRow - Feature list item with inline editing, auto-save, and creation support.
 *
 * A unified component for both displaying/editing saved features and creating new ones.
 * In 'edit' mode: Uses form state, auto-saves changes, shows drag handle and delete button.
 * In 'create' mode: Uses local state, submits on Enter/click, shows "+" icon for creation.
 */

import { useState, useCallback, useRef, useMemo, memo } from 'react';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import { Trash2, Loader2, Plus } from 'lucide-react';
import { IconButton } from '@lean-agility/design-system/react';
import { DatePicker } from '@lean-agility/design-system/react';
import {
  SortableHandle,
  SaveStatusIndicator,
  SourceIdBadge,
  FeatureStatusBadge,
  FeatureTeamChips,
  InlineInput,
} from '@/components/atoms';
import type { SimulationFormValues } from '@/types/api';
import { deleteFeature, updateFeature, createFeature } from '@/lib/api';
import { teamsForFeature } from '@/lib/utils';
import { useErrorHandler, useAutoSave } from '@/hooks';
import { useWriteErrors } from '@/hooks/useWriteErrors';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@lean-agility/design-system/react';

interface FeatureRowBaseProps {
  /** Simulation ID for API calls */
  simulationId: string | null;
}

interface FeatureRowEditProps extends FeatureRowBaseProps {
  mode: 'edit';
  /** dnd-kit field ID */
  id: string;
  /** Index in the features array */
  index: number;
  /** Database UUID of the feature (required in edit mode) */
  featureId: string;
  /** Callback to remove this feature from the list */
  onRemove: () => void;
  /** Whether removal is allowed */
  canRemove: boolean;
}

interface FeatureRowCreateProps extends FeatureRowBaseProps {
  mode: 'create';
  id?: never;
  index?: never;
  featureId?: never;
  onRemove?: never;
  canRemove?: never;
}

export type FeatureRowProps = FeatureRowEditProps | FeatureRowCreateProps;

/**
 * FeatureRow - Unified feature row for edit and create modes.
 *
 * @example Edit mode
 * ```tsx
 * <FeatureRow
 *   mode="edit"
 *   id={field.id}
 *   index={index}
 *   featureId={features[index].id}
 *   onRemove={() => remove(index)}
 *   canRemove={true}
 *   simulationId={simulationId}
 * />
 * ```
 *
 * @example Create mode
 * ```tsx
 * <FeatureRow mode="create" simulationId={simulationId} />
 * ```
 */
export const FeatureRow = memo(function FeatureRow(props: FeatureRowProps) {
  if (props.mode === 'create') {
    return <FeatureRowCreate simulationId={props.simulationId} />;
  }
  return <FeatureRowEdit {...props} />;
});

// ============================================================================
// Edit Mode Component
// ============================================================================

function FeatureRowEdit({ id, index, featureId, onRemove, canRemove, simulationId }: FeatureRowEditProps) {
  const {
    register,
    formState: { errors, touchedFields },
    control,
  } = useFormContext<SimulationFormValues>();

  // Get feature values for error context and save tracking
  const currentName = useWatch({ control, name: `features.${index}.name` }) ?? '';
  const currentSourceId = useWatch({ control, name: `features.${index}.source_id` });
  const currentSourceUrl = useWatch({ control, name: `features.${index}.source_url` });
  const currentDueDate = useWatch({ control, name: `features.${index}.due_date` });
  const currentStatus = useWatch({ control, name: `features.${index}.status` });
  // Which teams this feature spans — derived from the deliverables the form
  // already holds, so it agrees with the deliverables panel, unsaved edits
  // included. (Watching `teams` here costs no re-render the row was not
  // already paying: `memo` is defeated by the parent's fresh `onRemove`.)
  const teams = useWatch({ control, name: 'teams' });
  const featureTeams = useMemo(() => teamsForFeature(teams ?? [], featureId), [teams, featureId]);
  const { handleError } = useErrorHandler(`Feature: ${currentName || `Feature ${index + 1}`}`);
  const { onSaveError } = useWriteErrors('feature', handleError);
  const [isDeleting, setIsDeleting] = useState(false);

  // Unified auto-save hook - replaces ~60 lines of manual logic
  const { status: saveStatus, saveNow } = useAutoSave({
    values: { name: currentName, due_date: currentDueDate },
    fields: [
      { name: 'name', skipIf: (v) => !(v as string)?.trim() },
      { name: 'due_date', transform: (v) => v || null },
    ],
    saveFn: (payload) => updateFeature(featureId, payload),
    delay: 1500,
    enabled: !!simulationId,
    onError: onSaveError,
  });

  // Save immediately on blur
  const handleBlur = useCallback(() => {
    void saveNow();
  }, [saveNow]);

  // Sortable hook for drag-and-drop
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const featureError = errors.features?.[index];
  const featureTouched = touchedFields.features?.[index];

  const handleRemove = async () => {
    if (simulationId) {
      setIsDeleting(true);
      try {
        await deleteFeature(featureId);
        onRemove();
      } catch (err) {
        setIsDeleting(false);
        handleError(err);
      }
    } else {
      onRemove();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex gap-0 transition-all duration-200"
      data-testid={`feature-row-${index}`}
    >
      {/* Gutter - List controls (drag handle + index) */}
      <div
        className={cn(
          `
            flex w-14 shrink-0 cursor-grab flex-row items-center justify-center gap-1 rounded-l-lg border-y border-l
            transition-colors
            active:cursor-grabbing
          `,
          isDragging
            ? 'border-border-accent bg-bg-accent-muted/40'
            : `
              border-border-default/30 bg-bg-elevated
              hover:bg-bg-muted/20
            `,
        )}
        {...attributes}
        {...listeners}
      >
        <SortableHandle />
        <div
          className="
            flex size-6 items-center justify-center rounded-md border border-border-default/60 bg-bg-subtle text-xs
            font-bold text-text-secondary shadow-xs
          "
        >
          {isDeleting ? (
            <Loader2 className="size-3 animate-spin text-text-subtle" />
          ) : (
            <SaveStatusIndicator status={saveStatus} idleContent={index + 1} />
          )}
        </div>
      </div>

      {/* Card - Content */}
      <div
        className={cn(
          `flex flex-1 items-center gap-3 rounded-r-lg border-y border-r px-3 py-2.5 transition-all duration-200`,
          isDragging
            ? 'border-border-accent bg-bg-elevated shadow-lg'
            : `
              border-border-default/30 bg-bg-elevated
              hover:border-border-default/50 hover:shadow-xs
            `,
        )}
      >
        {/* Source ID Badge — a direct flex child, like DeliverableForm's, so the
            row's `items-center` centres the badge itself. Wrapped in a block
            <div> it was centring the wrapper instead, and the badge (being
            inline-flex) then sat on that wrapper's text baseline, leaving the
            line box's descender space below it. Measured: 2.5px below the
            row's centreline with the wrapper, 0 without it. */}
        {currentSourceId && (
          <SourceIdBadge sourceId={currentSourceId} href={currentSourceUrl} size="sm" className="shrink-0" />
        )}

        {/* Feature Name Input */}
        <div className="flex-1">
          <InlineInput
            type="text"
            size="sm"
            placeholder="Feature name"
            disabled={isDeleting}
            title={featureError?.name?.message || currentName || undefined}
            error={!!featureError?.name && !!featureTouched?.name}
            {...register(`features.${index}.name`, {
              onBlur: handleBlur,
            })}
          />
        </div>

        {/* Tracker status badge (tracker-owned, written by the feature import) */}
        <FeatureStatusBadge status={currentStatus} />

        {/* Teams with a deliverable grouped under this feature (derived, never stored) */}
        <FeatureTeamChips teams={featureTeams} />

        {/* Due Date Picker */}
        <div className="w-32 shrink-0">
          <Controller
            name={`features.${index}.due_date`}
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                value={field.value || ''}
                className="w-full"
                placeholder="Due date"
                clearable
                showToday={false}
                onBlur={handleBlur}
              />
            )}
          />
        </div>

        {/* Hidden fields for ID, source_id, and source_url (preserved through edits) */}
        <input type="hidden" {...register(`features.${index}.id`)} />
        <input type="hidden" {...register(`features.${index}.source_id`)} />
        <input type="hidden" {...register(`features.${index}.source_url`)} />

        {/* Remove Button */}
        {canRemove && (
          <IconButton
            type="button"
            variant="ghost-danger"
            size="sm"
            onClick={handleRemove}
            disabled={isDeleting}
            className="shrink-0"
            aria-label="Delete feature"
          >
            {isDeleting ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
          </IconButton>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Create Mode Component
// ============================================================================

function FeatureRowCreate({ simulationId }: { simulationId: string | null }) {
  const [name, setName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { handleError } = useErrorHandler('Feature Creation');

  const clearForm = () => {
    setName('');
    setDueDate('');
    setError(null);
  };

  const handleSubmit = async () => {
    if (!simulationId) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await createFeature(simulationId, {
        name: trimmedName,
        due_date: dueDate || null,
      });
      // Clear form - ready for next feature
      clearForm();
      // Re-focus input for quick consecutive entries
      inputRef.current?.focus();
    } catch (err) {
      handleError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      clearForm();
    }
  };

  const canSubmit = name.trim().length > 0 && !isSubmitting;

  return (
    <div className="group flex gap-0 transition-all duration-200" data-testid="feature-row-create">
      {/* Gutter - "+" indicator (no drag for create mode) */}
      <div
        className="
          flex w-14 shrink-0 flex-row items-center justify-center gap-1 rounded-l-lg border-y border-l
          border-border-default/20 bg-bg-elevated transition-colors
          group-hover:border-border-default/30
        "
      >
        {/* Invisible spacer matching SortableHandle width for alignment */}
        <div className="w-4" />
        <div
          className="
            flex size-6 items-center justify-center rounded-md border border-border-default/40 bg-bg-muted/40 text-sm
            font-normal text-text-muted shadow-xs
            dark:bg-bg-muted/30
          "
        >
          +
        </div>
      </div>

      {/* Card - Content */}
      <div
        className="
          flex flex-1 items-center gap-3 rounded-r-lg border-y border-r border-border-default/20 bg-bg-elevated px-3
          py-2.5 transition-colors
          group-hover:border-border-default/30
        "
      >
        {/* Feature Name Input */}
        <div className="flex-1">
          <InlineInput
            ref={inputRef}
            type="text"
            size="sm"
            placeholder="New feature name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={handleKeyDown}
            disabled={isSubmitting}
            error={!!error}
            title={error || undefined}
          />
        </div>

        {/* Due Date Picker */}
        <div className="w-32 shrink-0">
          <DatePicker
            value={dueDate}
            onChange={setDueDate}
            className="w-full"
            placeholder="Due date"
            clearable
            showToday={false}
            disabled={isSubmitting}
          />
        </div>

        {/* Create Button - same position as delete button */}
        <IconButton
          type="button"
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="shrink-0"
          aria-label="Create feature"
        >
          {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
        </IconButton>
      </div>
    </div>
  );
}
